import React from 'react';
import { CODE_OF_ETHICS } from '../../constants';
import { 
  ShieldCheckIcon, 
  AcademicCapIcon, 
  CheckCircleIcon,
  DocumentTextIcon,
  LockClosedIcon,
  ScaleIcon,
  FlagIcon
} from '@heroicons/react/24/outline';

const MembershipCodeOfEthicsPage: React.FC = () => {
  const getEthicsIcon = (id: string) => {
    const iconProps = { className: "w-8 h-8" };
    
    switch (id) {
      case 'professional-excellence':
        return <ShieldCheckIcon {...iconProps} />;
      case 'confidentiality':
        return <LockClosedIcon {...iconProps} />;
      case 'reputation-protection':
        return <ScaleIcon {...iconProps} />;
      case 'continuing-education':
        return <AcademicCapIcon {...iconProps} />;
      case 'professional-advancement':
        return <FlagIcon {...iconProps} />;
      default:
        return <ShieldCheckIcon {...iconProps} />;
    }
  };

  const getEthicsColor = (id: string) => {
    switch (id) {
      case 'professional-excellence':
        return 'bg-green-100 text-green-600 border-green-200';
      case 'confidentiality':
        return 'bg-blue-100 text-blue-600 border-blue-200';
      case 'reputation-protection':
        return 'bg-purple-100 text-purple-600 border-purple-200';
      case 'continuing-education':
        return 'bg-orange-100 text-orange-600 border-orange-200';
      case 'professional-advancement':
        return 'bg-primary-100 text-primary-600 border-primary-200';
      default:
        return 'bg-gray-100 text-gray-600 border-gray-200';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="container-max section-padding">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Code of Ethics
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our commitment to maintaining the highest standards of professional conduct and ethical behavior
            </p>
          </div>
        </div>
      </div>

      {/* Introduction */}
      <div className="section-padding">
        <div className="container-max">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-xl shadow-lg p-8 mb-12">
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <DocumentTextIcon className="w-10 h-10 text-primary-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  Our Ethical Foundation
                </h2>
              </div>
              
              <div className="prose prose-lg max-w-none">
                <p className="text-gray-600 leading-relaxed mb-6">
                  The primary goal of the Cyber Security Experts Association of Nigeria (CSEAN) is to promote 
                  best practices in the field of Information Security in Nigeria. To achieve this goal, members 
                  of the Association must reflect the highest standards of ethical conduct.
                </p>
                
                <p className="text-gray-600 leading-relaxed mb-6">
                  Therefore, CSEAN has established the following Code of Ethics and requires its observance as a 
                  prerequisite for continued membership and affiliation with the Association. Consequently, all 
                  CSEAN members must agree to abide by the following Code of Ethics:
                </p>
                
                <p className="text-gray-600 leading-relaxed">
                  All in a view to transforming the Information Security practices' landscape culturally and 
                  professionally in Nigeria.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Ethics Principles */}
      <div className="section-padding bg-white">
        <div className="container-max">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              CSEAN Code of Ethics
            </h2>
            <p className="text-xl text-gray-600">
              Five fundamental principles that guide our professional conduct
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8">
            {CODE_OF_ETHICS.map((ethics, index) => (
              <div
                key={ethics.id}
                className="card hover:shadow-xl transition-all duration-300"
              >
                <div className="flex items-start mb-6">
                  <div className={`p-3 rounded-lg mr-4 border-2 ${getEthicsColor(ethics.id)}`}>
                    {getEthicsIcon(ethics.id)}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">
                      {ethics.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {ethics.description}
                    </p>
                  </div>
                </div>

                <div className="space-y-3">
                  <h4 className="font-semibold text-gray-900 mb-3">
                    Key Principles:
                  </h4>
                  {ethics.principles.map((principle, principleIndex) => (
                    <div key={principleIndex} className="flex items-start">
                      <CheckCircleIcon className="w-5 h-5 text-primary-500 mt-0.5 mr-3 flex-shrink-0" />
                      <span className="text-gray-700">{principle}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Ethical Guidelines */}
      <div className="section-padding">
        <div className="container-max">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Professional Guidelines
              </h2>
              <p className="text-xl text-gray-600">
                Specific guidelines for ethical behavior in cybersecurity practice
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="card">
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  Client Confidentiality
                </h3>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start">
                    <CheckCircleIcon className="w-5 h-5 text-primary-500 mt-0.5 mr-2 flex-shrink-0" />
                    Protect all confidential information and data
                  </li>
                  <li className="flex items-start">
                    <CheckCircleIcon className="w-5 h-5 text-primary-500 mt-0.5 mr-2 flex-shrink-0" />
                    Obtain proper authorization before sharing information
                  </li>
                  <li className="flex items-start">
                    <CheckCircleIcon className="w-5 h-5 text-primary-500 mt-0.5 mr-2 flex-shrink-0" />
                    Maintain secure communication channels
                  </li>
                </ul>
              </div>

              <div className="card">
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  Professional Development
                </h3>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start">
                    <CheckCircleIcon className="w-5 h-5 text-primary-500 mt-0.5 mr-2 flex-shrink-0" />
                    Continuously update technical knowledge
                  </li>
                  <li className="flex items-start">
                    <CheckCircleIcon className="w-5 h-5 text-primary-500 mt-0.5 mr-2 flex-shrink-0" />
                    Participate in professional training and certification
                  </li>
                  <li className="flex items-start">
                    <CheckCircleIcon className="w-5 h-5 text-primary-500 mt-0.5 mr-2 flex-shrink-0" />
                    Share knowledge with the community
                  </li>
                </ul>
              </div>

              <div className="card">
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  Responsible Disclosure
                </h3>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start">
                    <CheckCircleIcon className="w-5 h-5 text-primary-500 mt-0.5 mr-2 flex-shrink-0" />
                    Follow responsible disclosure practices
                  </li>
                  <li className="flex items-start">
                    <CheckCircleIcon className="w-5 h-5 text-primary-500 mt-0.5 mr-2 flex-shrink-0" />
                    Report vulnerabilities through proper channels
                  </li>
                  <li className="flex items-start">
                    <CheckCircleIcon className="w-5 h-5 text-primary-500 mt-0.5 mr-2 flex-shrink-0" />
                    Avoid malicious or harmful activities
                  </li>
                </ul>
              </div>

              <div className="card">
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  Community Contribution
                </h3>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start">
                    <CheckCircleIcon className="w-5 h-5 text-primary-500 mt-0.5 mr-2 flex-shrink-0" />
                    Mentor emerging professionals
                  </li>
                  <li className="flex items-start">
                    <CheckCircleIcon className="w-5 h-5 text-primary-500 mt-0.5 mr-2 flex-shrink-0" />
                    Contribute to industry standards and best practices
                  </li>
                  <li className="flex items-start">
                    <CheckCircleIcon className="w-5 h-5 text-primary-500 mt-0.5 mr-2 flex-shrink-0" />
                    Support diversity and inclusion in cybersecurity
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Agreement Section */}
      <div className="bg-primary-600 section-padding">
        <div className="container-max text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Commitment to CSEAN Excellence
          </h2>
          <p className="text-xl text-primary-100 mb-8 max-w-3xl mx-auto">
            By joining CSEAN, you commit to upholding these ethical standards and contributing 
            to the transformation of Information Security practices' landscape culturally and 
            professionally in Nigeria.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/membership/application"
              className="bg-white text-primary-600 hover:bg-gray-50 font-semibold py-3 px-8 rounded-lg transition-colors duration-200 inline-flex items-center justify-center"
            >
              Join CSEAN Today
            </a>
            <a
              href="/membership/benefits"
              className="border-2 border-white text-white hover:bg-white hover:text-primary-600 font-semibold py-3 px-8 rounded-lg transition-colors duration-200 inline-flex items-center justify-center"
            >
              View Benefits
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MembershipCodeOfEthicsPage;
